import { customerApiEndpoints } from '@/api/endpoints';
import { useGetApiList, useMutateGetApi } from '@/apiError/apiShow';
import { useUpdateApi } from '@/apiError/apiUpdate';
import { AntButton } from '@/common/ant-components/AntButton';
import { useFetch } from '@/hooks';
import { rules } from '@/utils/validation/FormValidation';
import { Col, DatePicker, Form, Modal, Row, Select } from 'antd';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

const BranchVersionModal = ({
  isOpen,
  setIsOpen,
  refetch,
  id = null,
  setEditReferenceId,
}) => {
  const [form] = Form.useForm();
  const minDate = Form.useWatch('subscriptionStart', form);
  const maxDate = Form.useWatch('subscriptionEnd', form);
  const [branchList, setBranchList] = useState();
  const { mutateAsync: getBranchList, isPending: loading } = useMutateGetApi();

  const { data } = useFetch(customerApiEndpoints.superAdminRetrieveList);

  const { mutateAsync: updateSubscription, isPending: updating } =
    useUpdateApi();

  const { data: versionData, isPending } =
    useGetApiList(`superadmin/versions/`);

  const versionOptions = versionData?.data?.map((version) => ({
    label: version?.versionName,
    value: version?.referenceId,
  }));

  const userOptions = data.map((user) => ({
    value: user.referenceId,
    label: `${user.firstName} ${user.lastName}`,
  }));

  const dateFormatter = (date) => {
    const month = `${date?.$M + 1}`.padStart(2, 0);
    const day = `${date?.$D}`.padStart(2, 0);
    const formattedDate = `${date?.$y}-${month}-${day}`;
    return formattedDate;
  };

  const handleSubmit = async (values) => {
    const { adminId, branchId, versionId, subscriptionStart, subscriptionEnd } =
      values;
    const formattedStartDate = dateFormatter(subscriptionStart);
    const formattedEndDate = dateFormatter(subscriptionEnd);
    const data = await updateSubscription({
      url: `superadmin/branch-versions/${branchId}/`,
      values: {
        versionId: versionId,
        subscriptionStartYear: formattedStartDate,
        subscriptionEndYear: formattedEndDate,
      },
    });
    setIsOpen(false);
    form.resetFields();
    setEditReferenceId && setEditReferenceId(null);
    refetch();
  };
  const getBranch = async (adminId) => {
    const res = await getBranchList({
      url: `/superadmin/branch/access/list/?adminId=${adminId}`,
    });
    const branchListOptions = res?.data?.map((branch) => ({
      label: branch?.name,
      value: branch?.referenceId,
    }));
    setBranchList(branchListOptions);
  };

  async function getData() {
    const res = await getBranchList({
      url: `superadmin/branch-versions/${id}/`,
    });
    const branchList = await getBranchList({
      url: `/superadmin/branch/access/list/?adminId=${res?.data?.adminId}`,
    });
    const branchListOptions = branchList?.data?.map((branch) => ({
      label: branch?.name,
      value: branch?.referenceId,
    }));
    setBranchList(branchListOptions);

    const {
      adminId,
      branchId,
      versionId,
      subscriptionStartYear,
      subscriptionEndYear,
    } = res?.data;

    form.setFieldsValue({
      adminId,
      branchId,
      versionId,
      subscriptionStart: dayjs(subscriptionStartYear.split('T')[0]),
      subscriptionEnd: dayjs(subscriptionEndYear.split('T')[0]),
    });
  }
  useEffect(() => {
    if (!id) return;
    if (isOpen && id) {
      getData();
    }
  }, [isOpen, id]);
  return (
    <div>
      <Modal
        open={isOpen}
        size="large"
        width={1000}
        onCancel={() => {
          setIsOpen(false);
          form.resetFields();
          setEditReferenceId && setEditReferenceId(null);
        }}
        onClose={() => {
          setIsOpen(false);
          form.resetFields();
          setEditReferenceId && setEditReferenceId(null);
        }}
        title={id !== null ? 'Update' : 'Create'}
        footer={[
          <AntButton
            key="back"
            onClick={() => {
              setIsOpen(false);
              form.resetFields();
              setEditReferenceId && setEditReferenceId(null);
            }}
            color="red"
          >
            Cancel
          </AntButton>,
          <AntButton key="submit" type="primary" onClick={() => form.submit()}>
            {id !== null ? 'Update' : 'Submit'}
          </AntButton>,
        ]}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={[16, 16]}>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                name="adminId"
                label="Admin User"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  placeholder="Select Admin"
                  onSelect={getBranch}
                  options={userOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 16 }}>
              <Form.Item
                name="branchId"
                label="Branch"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  placeholder="Select Branch"
                  disabled={!branchList?.length}
                  options={branchList?.length ? branchList : []}
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                name="versionId"
                label="Version"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  placeholder="Select Version"
                  options={versionOptions?.length ? versionOptions : []}
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                name="subscriptionStart"
                label="Subscription Start"
                rules={rules.required}
                hasFeedback
              >
                <DatePicker
                  maxDate={maxDate && maxDate}
                  onChange={(value) => {
                    const date = dayjs(dateFormatter(value)); // or your object if already dayjs
                    const newDate = date.add(1, 'year');

                    form.setFieldsValue({
                      subscriptionEnd: newDate, // Make sure the field is expecting a dayjs object
                    });
                  }}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                name="subscriptionEnd"
                label="Subscription End"
                rules={rules.required}
                hasFeedback
              >
                <DatePicker
                  minDate={minDate && minDate}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default BranchVersionModal;
